import { createContext, useContext, useEffect, useRef, useState } from "react";
import api, { formatApiError, getToken, setToken, clearToken, isImpersonating } from "@/lib/api";
import { toast } from "sonner";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const pingRef = useRef(null);

  const startShiftPing = () => {
    if (pingRef.current) return;
    // Every 60s: /auth/me. If backend kicks out (401 outside shift), logout the UI.
    pingRef.current = setInterval(async () => {
      try {
        await api.get("/auth/me");
      } catch (err) {
        if (err?.response?.status === 401) {
          const msg = err?.response?.data?.detail || "Sessão encerrada.";
          clearToken();
          setUser(null);
          try { toast.error(msg); } catch { /* ignore */ }
          if (pingRef.current) {
            clearInterval(pingRef.current);
            pingRef.current = null;
          }
        }
      }
    }, 60_000);
  };

  const stopShiftPing = () => {
    if (pingRef.current) {
      clearInterval(pingRef.current);
      pingRef.current = null;
    }
  };

  useEffect(() => {
    const token = getToken();
    if (!token) {
      setLoading(false);
      return;
    }
    api.get("/auth/me")
      .then((res) => { setUser(res.data); startShiftPing(); })
      .catch(() => clearToken())
      .finally(() => setLoading(false));
    return stopShiftPing;
  }, []);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    if (data.mfa_required) {
      return { mfaRequired: true, email: data.email || email, password };
    }
    setToken(data.token);
    setUser(data.user);
    startShiftPing();
    return data.user;
  };

  const loginMfa = async (email, password, mfaCode) => {
    const { data } = await api.post("/auth/login/mfa", { email, password, mfa_code: mfaCode });
    setToken(data.token);
    setUser(data.user);
    startShiftPing();
    return data.user;
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch { /* ignore */ }
    clearToken();
    setUser(null);
    stopShiftPing();
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, loginMfa, logout, formatApiError, isImpersonating: isImpersonating() }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
